import {
  zoomTransform,
  event,
  select,
  format,
  timeFormat,
  axisRight,
  line as lineD3,
  axisLeft,
  selectAll,
} from "d3";
import {
  colorDarkExpo,
  colorHedge,
  colorSymmetryH,
  grayLightBackground,
  textactive2,
} from "@/_core/_helpers/_parametric_tools";
import {
  dotCreate,
  hLineCreate,
  vLineCreate,
} from "@/_core/charts/D3ChartHoverHelpers";
import {
  create2YTooltips,
  inRange,
  mouseDate,
  roundDate,
  zoomSetup,
} from "@/_core/charts/TechnicalAnalysis";
import { populateTableComparison } from "@/_core/data/Markets";
import { regression } from "@/_core/_helpers/_quadraticVolatility";

export function ComparisonHover(_this, isTwoAxes, isTwoSeries, colorTheme) {
  const auxName = "TA";
  let valueLast = _this.chartDataRaw[_this.chartDataRaw.length - 1].macd;
  let xTooltipWidth = 80;
  let yTooltip = create2YTooltips(
    _this.svgNode,
    _this.currencyFormat,
    valueLast,
    xTooltipWidth,
    colorDarkExpo(colorTheme),
    colorHedge(colorTheme),
  );
  let y2Tooltip = yTooltip["y2Tooltip"];
  let xTooltip = yTooltip["xTooltip"];
  yTooltip = yTooltip["yTooltip"];
  hLineCreate(_this.svgNode, auxName, grayLightBackground(colorTheme), 1);
  vLineCreate(_this.svgNode, auxName, grayLightBackground(colorTheme), 1);
  dotCreate(
    _this.svgNode,
    auxName,
    3,
    textactive2(colorTheme),
    colorDarkExpo(colorTheme),
    3,
  );
  dotCreate(
    _this.svgNode,
    auxName + 2,
    3,
    textactive2(colorTheme),
    colorHedge(colorTheme),
    3,
  );

  function mouseover() {
    if (_this.transform === undefined || _this.transform == null) {
      _this.transform = zoomTransform(
        select(".hoverParent" + auxName)._groups[0][0],
      );
    }
    event.preventDefault();
    const xPixels = Math.max(
      Math.min(event.layerX - _this.svgNode.margin.left, _this.svgNode.width),
      0,
    );
    var xt = _this.transform.rescaleX(_this.svgNode.x);
    var yt = _this.svgNode.y;
    var yt2 = isTwoAxes ? _this.svgNode.y2 : _this.svgNode.y;
    let dat = roundDate(xt.invert(xPixels));
    let selData;

    let highlightCandles = true;
    if (dat <= _this.chartDataRaw[_this.chartDataRaw.length - 1].date) {
      selData = mouseDate(xt.invert(xPixels), _this.chartDataRaw);
    } else {
      selData = JSON.parse(
        JSON.stringify(_this.chartDataRaw[_this.chartDataRaw.length - 1]),
      );
      selData["date"] = dat;
      highlightCandles = false;
    }
    const yPixels = Math.max(
      Math.min(event.layerY - _this.svgNode.margin.top, _this.svgNode.height),
      0,
    );
    let yRate = yt.invert(yPixels);
    let y2Rate = yt2.invert(yPixels);
    yTooltip
      .style("opacity", 1)
      .style(
        "right",
        _this.svgNode.width + _this.svgNode.margin.right + 2 + "px",
      )
      .style("top", yPixels + _this.svgNode.margin.top - 11 + "px")
      .html(format(_this.currencyFormat)(yRate));
    y2Tooltip
      .style("opacity", 1)
      .style("left", _this.svgNode.width + _this.svgNode.margin.left + 2 + "px")
      .style("top", yPixels + _this.svgNode.margin.top - 11 + "px")
      .html(format(_this.currencyFormat)(y2Rate));
    xTooltip
      .style("opacity", 1)
      .style(
        "left",
        xt(selData.date) + _this.svgNode.margin.left - xTooltipWidth / 2 + "px",
      )
      .style("top", _this.svgNode.height + _this.svgNode.margin.top + 2 + "px")
      .html(timeFormat(_this.dateFormat)(selData.date));

    select(".hHoverLineTA")
      .style("opacity", 1)
      .attr("y1", yPixels)
      .attr("y2", yPixels);

    let xPix = xt(selData.date);

    select(".vHoverLineTA")
      .style("opacity", 1)
      .attr("x1", xPix)
      .attr("x2", xPix);
    select(".vHoverLineSpread")
      .style("opacity", 1)
      .attr("x1", xPix)
      .attr("x2", xPix);
    select(".vHoverLineCorrel")
      .style("opacity", 1)
      .attr("x1", xPix)
      .attr("x2", xPix);

    if (highlightCandles) {
      select(".dotTA")
        .style("opacity", 1)
        .attr(
          "transform",
          `translate(${xt(selData.date)},${yt(selData[_this.fieldUp])})`,
        );
      if (isTwoSeries) {
        select(".dotTA2")
          .style("opacity", 1)
          .attr(
            "transform",
            `translate(${xt(selData.date)},${yt2(selData[_this.fieldUp2])})`,
          );
      }
      const yt3 = _this.svgNodeSpread.y;
      select(".dotSpread")
        .style("opacity", 1)
        .attr(
          "transform",
          `translate(${xt(selData.date)},${yt3(
            selData[_this.spreadFieldName],
          )})`,
        );
      const yt4 = _this.svgNodeCorrel.y;
      select(".dotCorrel")
        .style("opacity", 1)
        .attr(
          "transform",
          `translate(${xt(selData.date)},${yt4(selData["corr"])})`,
        );
    } else {
      select(".dotTA").style("opacity", 0);
      if (isTwoSeries) {
        select(".dotTA2").style("opacity", 0);
      }
      select(".dotSpread").style("opacity", 0);
      select(".dotCorrel").style("opacity", 0);
    }

    populateTableComparison(
      {
        date: selData.date,
        main: selData[_this.fieldUp],
        second: selData[_this.fieldUp2],
        spread: selData[_this.spreadFieldName],
        corr: selData.corr,
      },
      format(_this.currencyFormat),
      timeFormat("%d %b %y"),
    );
  }
  function mouseout() {
    yTooltip
      .style("right", "-1000000px")
      .style("top", "0px")
      .style("opacity", 0)
      .html("");
    y2Tooltip
      .style("left", "-1000000px")
      .style("top", "0px")
      .style("opacity", 0)
      .html("");
    xTooltip
      .style("left", "-1000000px")
      .style("top", "0px")
      .style("opacity", 0)
      .html("");
    select(".hHoverLineTA").style("opacity", 0);
    select(".vHoverLineTA").style("opacity", 0);
    select(".vHoverLineSpread").style("opacity", 0);
    select(".vHoverLineCorrel").style("opacity", 0);
    select(".dotTA").style("opacity", 0);
    if (isTwoSeries) {
      select(".dotTA2").style("opacity", 0);
    }
    select(".dotSpread").style("opacity", 0);
    select(".dotCorrel").style("opacity", 0);
    let d = _this.chartDataRaw[_this.chartDataRaw.length - 1];
    populateTableComparison(
      {
        date: d.date,
        main: d[_this.fieldUp],
        second: d[_this.fieldUp2],
        spread: d[_this.spreadFieldName],
        corr: d.corr,
      },
      format(_this.currencyFormat),
      timeFormat("%b %d, %y"),
    );
  }
  function zoomed() {
    if (!_this.keepDateInterval) {
      setTimeout(() => {
        _this.pickedDateInterval = "-1";
      }, 500);
    }
    yTooltip
      .style("right", "-1000000px")
      .style("top", "0px")
      .style("opacity", 0)
      .html("");
    y2Tooltip
      .style("left", "-1000000px")
      .style("top", "0px")
      .style("opacity", 0)
      .html("");
    xTooltip
      .style("left", "-1000000px")
      .style("top", "0px")
      .style("opacity", 0)
      .html("");

    select(".hHoverLineTA").style("opacity", 0);
    select(".vHoverLineTA").style("opacity", 0);
    select(".vHoverLineSpread").style("opacity", 0);
    select(".vHoverLineCorrel").style("opacity", 0);

    select(".dotTA").style("opacity", 0);
    if (isTwoSeries) {
      select(".dotTA2").style("opacity", 0);
    }
    select(".dotSpread").style("opacity", 0);
    select(".dotCorrel").style("opacity", 0);

    _this.transform = event.transform;
    _this.svgNode.gX.call(
      _this.svgNode.xAxis.scale(_this.transform.rescaleX(_this.svgNode.x)),
    );
    const xt = _this.transform.rescaleX(_this.svgNode.x);
    TAZooming(_this.transform, xt, _this, isTwoSeries, isTwoAxes);

    // transform other charts
    if (_this.externalZoom === false) {
      _this.externalZoom = true;
      select(".hoverParentSpread").call(
        _this.zoomSpread.transform,
        _this.transform,
      );
      select(".hoverParentCorrel").call(
        _this.zoomCorrel.transform,
        _this.transform,
      );
      _this.externalZoom = false;
    }
    _this.keepDateInterval = false;
  }
  function zoomEnd() {
    _this.transform = event.transform;
    const xt = _this.transform.rescaleX(_this.svgNode.x);
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(function() {
      var xmin = new Date(xt.invert(0));
      var xmax = new Date(xt.invert(_this.svgNode.width));

      if (!_this.externalZoom) {
        let data = _this.chartDataRaw.map((d) => d);
        _this.filteredData = data.filter(function(d) {
          return inRange(d.date, xmin, xmax);
        });
        if (
          _this.selectedMethod === 1 ||
          _this.selectedMethod === 5 ||
          _this.selectedMethod === 6
        ) {
          let init1 = _this.filteredData[0].close;
          let init2 = _this.filteredData[0].close2;
          _this.filteredData.forEach(function(d) {
            d.close_100 = (100 * d.close) / init1;
            d.close2_100 = (100 * d.close2) / init2;
            d.mult = d.close * d.close2;
            d.div = d.close / d.close2;
          });
        } else if (_this.selectedMethod === 7) {
          _this.filteredData = regression(
            _this.filteredData,
            "close2",
            "close",
            "reg",
            "m",
            "b",
          );
        } else if (_this.selectedMethod === 8) {
          _this.filteredData = regression(
            _this.filteredData,
            "lnClose2",
            "lnClose",
            "lnReg",
            "lnM",
            "lnB",
          );
        }
      }

      let minMaxYAxis, minMaxYAxis2;
      minMaxYAxis = _this.getMinMaxYAxis();
      minMaxYAxis2 = [minMaxYAxis[2], minMaxYAxis[3]];
      minMaxYAxis = [minMaxYAxis[0], minMaxYAxis[1]];

      if (!isFinite(minMaxYAxis[0])) {
        minMaxYAxis[0] = -1;
      }
      if (!isFinite(minMaxYAxis[1])) {
        minMaxYAxis[1] = 1;
      }
      if (!isFinite(minMaxYAxis2[0])) {
        minMaxYAxis2[0] = -1;
      }
      if (!isFinite(minMaxYAxis2[1])) {
        minMaxYAxis2[1] = 1;
      }
      _this.svgNode.y.domain(minMaxYAxis);
      let yt = _this.svgNode.y;
      let yt2;
      if (isTwoAxes) {
        _this.svgNode.y2.domain(minMaxYAxis2);
        yt2 = _this.svgNode.y2;
      } else {
        yt2 = _this.svgNode.y;
      }

      TAZoomEnd(
        _this.transform,
        xt,
        yt,
        yt2,
        _this.filteredData,
        _this,
        isTwoSeries,
        isTwoAxes,
        colorTheme,
      );
    }, 200);
  }

  var resizeTimer;
  zoomSetup(
    _this,
    "svgNode",
    "zoom" + auxName,
    auxName,
    "",
    mouseover,
    mouseout,
    zoomed,
    zoomEnd,
  );
  let d = _this.chartDataRaw[_this.chartDataRaw.length - 1];
  populateTableComparison(
    {
      date: d.date,
      main: d[_this.fieldUp],
      second: d[_this.fieldUp2],
      spread: d[_this.isLn ? "lnSpread" : "spread"],
      corr: d.corr,
    },
    format(_this.currencyFormat),
    timeFormat("%d %b %y"),
  );
  select(".hoverParent" + auxName).call(
    _this.zoomTA.transform,
    _this.transform,
  );
}

export function TAZooming(t, xt, _this, isTwoSeries, isTwoAxes) {
  let line;
  line = lineD3()
    .defined((d) => !Number.isNaN(d[_this.fieldUp]))
    .x((d) => xt(d.date))
    .y((d) => _this.svgNode.y(d[_this.fieldUp]));
  _this.svgNode["close"].attr("d", (d) => line(d));
  if (isTwoSeries) {
    if (isTwoAxes) {
      line = lineD3()
        .defined((d) => !Number.isNaN(d[_this.fieldUp2]))
        .x((d) => xt(d.date))
        .y((d) => _this.svgNode.y2(d[_this.fieldUp2]));
    } else {
      line = lineD3()
        .defined((d) => !Number.isNaN(d[_this.fieldUp2]))
        .x((d) => xt(d.date))
        .y((d) => _this.svgNode.y(d[_this.fieldUp2]));
    }
    _this.svgNode["close2"].attr("d", (d) => line(d));
  }
}

export function TAZoomEnd(
  t,
  xt,
  yt,
  yt2,
  filtered,
  _this,
  isTwoSeries,
  isTwoAxes,
  colorTheme,
) {
  let line;
  line = lineD3()
    .defined((d) => !Number.isNaN(d[_this.fieldUp]))
    .x((d) => xt(d.date))
    .y((d) => yt(d[_this.fieldUp]));
  _this.svgNode["close"]
    .data([filtered])
    .join("path")
    .attr("d", (d) => line(d));
  if (isTwoSeries) {
    if (isTwoAxes) {
      line = lineD3()
        .defined((d) => !Number.isNaN(d[_this.fieldUp2]))
        .x((d) => xt(d.date))
        .y((d) => yt2(d[_this.fieldUp2]));
    } else {
      line = lineD3()
        .defined((d) => !Number.isNaN(d[_this.fieldUp2]))
        .x((d) => xt(d.date))
        .y((d) => yt(d[_this.fieldUp2]));
    }
    _this.svgNode["close2"]
      .data([filtered])
      .join("path")
      .attr("d", (d) => line(d));
  } else {
    _this.svgNode["zeroLineTA"].attr("y1", yt(0)).attr("y2", yt(0));
  }

  if (_this.selectedMethod === 1) {
    _this.svgNode["hundredLine"].attr("y1", yt(100)).attr("y2", yt(100));
  }

  // y axis
  let tickCount = _this.svgNode.height / 40;
  _this.svgNode.gY
    .transition()
    .duration(500)
    .call(
      axisLeft()
        .tickSize(-_this.svgNode.width)
        .ticks(tickCount)
        .tickSizeOuter(0)
        .tickPadding(10)
        .ticks(tickCount)
        .scale(yt),
    );
  if (isTwoAxes) {
    selectAll(".y-axis-1 path").style("stroke", colorDarkExpo(colorTheme));
    selectAll(".y-axis-1 text").style("fill", colorSymmetryH(colorTheme));
    _this.svgNode.gY2
      .transition()
      .duration(500)
      .call(
        axisRight()
          .tickSize(0)
          .ticks(tickCount)
          .tickSizeOuter(0)
          .tickPadding(10)
          .scale(yt2),
      );
    selectAll(".y-axis-2 path").style("stroke", colorHedge(colorTheme));
    selectAll(".y-axis-2 text").style("fill", colorHedge(colorTheme));
  }
}
